import { accountAccess } from '@app/+authenticated/+account/account.access';
import { kioskAccess } from '@app/+authenticated/+account/settings/+kiosk/kiosk.access';
import { employeeSettingsAccess } from '@app/+authenticated/+account/settings/+modules/employee-settings/employee-settings.access';
import { scheduleSettingsAccess } from '@app/+authenticated/+account/settings/+modules/schedule-settings/schedule-settings.access';
import { timetrackingSettingsAccess } from '@app/+authenticated/+account/settings/+modules/timetracking-settings/timetracking-settings.access';
import { accountSettingsAccess } from '@app/+authenticated/+account/settings/account-settings.access';
import { insightSettingsAccess } from '@app/+authenticated/+account/settings/insights/insight-settings.access';
import { settingsGroupsAccess } from '@app/+authenticated/shared/sidebar/settings-sidebar/settings-groups.access';
import { _ } from '@ngx-translate/core';

import type { MenuNode } from './command-palette.model';

/**
 * The Settings subtree of the command palette. Each item references the same
 * feature-owned access descriptor its route spreads into `data`. Group headers
 * carry the group gate; each leaf carries its own constraint too, because a leaf
 * can be gated more strictly than its group (e.g. a PREMIUM page under a BASIC
 * group). Leaves with no descriptor entry (the plain "General" pages) inherit the
 * group's Manage-account gate through the resolver's pruning.
 */
export const settingsMenuItems: MenuNode[] = [
  {
    slug: 'settings.general',
    titleKey: _('General'),
    access: accountSettingsAccess.settings,
    route: ['/account/settings'],
  },
  {
    slug: 'settings.subscription',
    titleKey: _('Subscription'),
    access: accountAccess.billing,
    route: ['/account/billing'],
  },
  {
    slug: 'settings.security',
    titleKey: _('Security'),
    keywordKeys: [_('permissions')],
    access: accountSettingsAccess.permissions,
    route: ['/account/settings/permissions'],
  },
  {
    slug: 'settings.organization',
    titleKey: _('Organization'),
    keywordKeys: [_('locations'), _('departments'), _('teams')],
    access: accountSettingsAccess.settings,
    route: ['/account/settings/locations'],
  },
  {
    slug: 'settings.shift-templates',
    titleKey: _('Shift templates'),
    access: accountSettingsAccess.settings,
    route: ['/account/settings/shifts'],
  },
  {
    slug: 'settings.insights',
    titleKey: _('Insights'),
    access: settingsGroupsAccess.insights,
    children: [
      {
        slug: 'settings.insights.notifications',
        titleKey: _('Notifications'),
        access: insightSettingsAccess.notifications,
        route: ['/account/settings/insights/notifications'],
      },
      {
        slug: 'settings.insights.targets',
        titleKey: _('Targets'),
        access: insightSettingsAccess.targets,
        route: ['/account/settings/insights/targets'],
      },
      {
        slug: 'settings.insights.turnover',
        titleKey: _('Turnover'),
        access: insightSettingsAccess.turnover,
        route: ['/account/settings/insights/turnover'],
      },
    ],
  },
  {
    slug: 'settings.employees',
    titleKey: _('Employees'),
    access: settingsGroupsAccess.employees,
    children: [
      {
        slug: 'settings.employees.notifications',
        titleKey: _('Notifications'),
        access: employeeSettingsAccess.notifications,
        route: ['/account/settings/modules/employees/notifications'],
      },
      {
        slug: 'settings.employees.custom-fields',
        titleKey: _('Custom fields'),
        access: employeeSettingsAccess['custom-fields'],
        route: ['/account/settings/modules/employees/custom-fields'],
      },
      {
        slug: 'settings.employees.contract-types',
        titleKey: _('Contract types'),
        access: employeeSettingsAccess['contract-types'],
        route: ['/account/settings/modules/employees/contract-types'],
      },
      {
        slug: 'settings.employees.overtime',
        titleKey: _('Overtime'),
        access: employeeSettingsAccess['overtime-policies'],
        route: ['/account/settings/modules/employees/overtime-policies'],
      },
      {
        slug: 'settings.employees.surcharges',
        titleKey: _('Surcharges'),
        access: employeeSettingsAccess.surcharges,
        route: ['/account/settings/modules/employees/surcharges'],
      },
      {
        slug: 'settings.employees.skills',
        titleKey: _('Skills'),
        access: employeeSettingsAccess.skills,
        route: ['/account/settings/modules/employees/skills'],
      },
      {
        slug: 'settings.employees.holidays',
        titleKey: _('Public holidays'),
        access: employeeSettingsAccess['holiday-groups'],
        route: ['/account/settings/modules/employees/holiday-groups'],
      },
    ],
  },
  {
    slug: 'settings.absence',
    titleKey: _('Absence'),
    access: accountSettingsAccess.settings,
    route: ['/account/settings/modules/absence'],
  },
  {
    slug: 'settings.time-tracking',
    titleKey: _('Time tracking'),
    access: settingsGroupsAccess.timeTracking,
    children: [
      {
        slug: 'settings.time-tracking.general',
        titleKey: _('General'),
        route: ['/account/settings/modules/timetracking/general'],
      },
      {
        slug: 'settings.time-tracking.notifications',
        titleKey: _('Notifications'),
        access: timetrackingSettingsAccess.notifications,
        route: ['/account/settings/modules/timetracking/notifications'],
      },
      {
        slug: 'settings.time-tracking.custom-fields',
        titleKey: _('Custom fields'),
        access: timetrackingSettingsAccess['custom-fields'],
        route: ['/account/settings/modules/timetracking/custom-fields'],
      },
      {
        slug: 'settings.time-tracking.clocking',
        titleKey: _('Clocking'),
        access: timetrackingSettingsAccess.clocking,
        route: ['/account/settings/modules/timetracking/clocking'],
      },
      {
        slug: 'settings.time-tracking.clock-boundaries',
        titleKey: _('Clock boundaries'),
        access: timetrackingSettingsAccess.restrictions,
        route: ['/account/settings/modules/timetracking/restrictions'],
      },
      {
        slug: 'settings.time-tracking.break-rules',
        titleKey: _('Break rules'),
        access: timetrackingSettingsAccess['break-rules'],
        route: ['/account/settings/modules/timetracking/break-rules'],
      },
      {
        slug: 'settings.time-tracking.kiosk',
        titleKey: _('Kiosk'),
        access: kioskAccess.kiosk,
        route: ['/account/settings/modules/kiosk'],
      },
    ],
  },
  {
    slug: 'settings.schedule',
    titleKey: _('Schedule'),
    access: settingsGroupsAccess.schedule,
    children: [
      {
        slug: 'settings.schedule.general',
        titleKey: _('General'),
        route: ['/account/settings/modules/schedule/general'],
      },
      {
        slug: 'settings.schedule.notifications',
        titleKey: _('Notifications'),
        access: scheduleSettingsAccess.notifications,
        route: ['/account/settings/modules/schedule/notifications'],
      },
      {
        slug: 'settings.schedule.custom-fields',
        titleKey: _('Custom fields'),
        access: scheduleSettingsAccess['custom-fields'],
        route: ['/account/settings/modules/schedule/custom-fields'],
      },
      {
        slug: 'settings.schedule.availability',
        titleKey: _('Availability'),
        access: scheduleSettingsAccess.availability,
        route: ['/account/settings/modules/schedule/availability'],
      },
    ],
  },
  {
    slug: 'settings.app-center',
    titleKey: _('App center'),
    access: accountAccess.integrations,
    route: ['/account/integrations'],
  },
];
